"use client";

import Image from "next/image";
import Link from "next/link";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/navbar/navigation-menu";
import { cn } from "@/lib/utils";

import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";

import { Banner, type BannerProps } from "./banner/banner";
import { iconMap } from "./banner/icon-map";
import { ListItem } from "./list-item";
import { type IconLink, type TextLink } from "./nav.config";

// Helper to render icon-based links
function ListItemWithIcon({ title, href, icon }: IconLink) {
  return (
    <li>
      <Link
        href={href}
        className="flex items-center gap-2 rounded-md p-3 transition-colors hover:bg-accent/50 focus:bg-accent/60"
      >
        {iconMap[icon]}
        <span className="text-sm font-medium">{title}</span>
      </Link>
    </li>
  );
}
const isMobile = false;
// Helper to render the dropdown content
const renderDesktopMenuContent = (
  links: readonly (TextLink | IconLink)[],
  banner?: BannerProps,
  key?: string,
) => {
  const MAX_SLOTS = 3;
  const left = links.slice(0, MAX_SLOTS);
  const right = links.slice(MAX_SLOTS);
  const twoColumns = links.length > MAX_SLOTS || banner;
  const gridClasses = twoColumns
    ? "lg:grid-cols-[.75fr_1fr]"
    : "lg:grid-cols-1";

  return (
    <ul
      className={cn(
        `grid gap-3 p-6 md:w-[400px] ${key === "attractions" || key === "forCompanies" || key === "forSchools" ? (key === "attractions" ? "c1400:w-[380px] c1500:w-[420] c1600:w-[470px] c1850:w-[600px] xl:w-[320px]" : key === "forCompanies" ? "c1400:w-[480px] c1500:w-[520] c1600:w-[570px] c1850:w-[600px] xl:w-[420px]" : "c1400:w-[590px] c1500:w-[600] xl:w-[530px]") : "lg:w-[600px]"} `,
        gridClasses,
      )}
    >
      <div className="flex flex-col gap-3">
        {left.map((l) =>
          "icon" in l ? (
            <ListItemWithIcon key={l.title} {...l} />
          ) : (
            <ListItem key={l.title} {...l}>
              {l.description}
            </ListItem>
          ),
        )}
      </div>
      {(right.length > 0 || !!banner) && (
        <div className="flex flex-col gap-3">
          {right.map((l) =>
            "icon" in l ? (
              <ListItemWithIcon key={l.title} {...l} />
            ) : (
              <ListItem key={l.title} {...l}>
                {l.description}
              </ListItem>
            ),
          )}
          {banner && (
            <li className="flex h-full items-center justify-center overflow-hidden p-2">
              <Banner {...banner} />
            </li>
          )}
        </div>
      )}
    </ul>
  );
};

export function NavbarDesktop({ items }: { items: any }) {
  return (
    <NavigationMenu className="ml-[62px] hidden xl:flex">
      {/* Left Items */}
      <NavigationMenuList>
        {(["forKids", "forAdults", "forStudents"] as const).map((key) => (
          <NavigationMenuItem key={key}>
            <NavigationMenuTrigger className="rounded-[8px] bg-transparent">
              {items[key].trigger}
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              {renderDesktopMenuContent(
                items[key].links,
                items[key].banner,
                key,
              )}
            </NavigationMenuContent>
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>

      {/* Logo */}
      <Link href="/" className="shrink-0">
        <Image
          src="/webp/logo-lasercamp-512px.webp"
          alt="LaserCamp logo"
          width={200}
          height={60}
          priority
        />
      </Link>

      {/* Right Items */}
      <NavigationMenuList>
        {(["forSchools", "forCompanies", "attractions"] as const).map((key) => (
          <NavigationMenuItem key={key}>
            <NavigationMenuTrigger className="rounded-[8px] bg-transparent">
              {items[key].trigger}
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              {renderDesktopMenuContent(
                items[key].links,
                items[key].banner,
                key,
              )}
            </NavigationMenuContent>
          </NavigationMenuItem>
        ))}
        <NavigationMenuItem>
          <Link
            href="/kontakt"
            className={cn(
              navigationMenuTriggerStyle,
              "cursor-pointer rounded-[8px] bg-transparent",
            )}
          >
            {items.contactLabel}
          </Link>
        </NavigationMenuItem>
      </NavigationMenuList>
      <LanguageSwitcher isMobile />
    </NavigationMenu>
  );
}
